import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout, { StatusBadge } from "../../components/admin/AdminLayout";
import { adminApi } from "../../lib/admin-api";
import { CheckCircle2, XCircle, AlertCircle, Send, Mail, Database, ShieldCheck, UserCheck, Globe, Clock, Megaphone, RefreshCw } from "lucide-react";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

export default function AdminDiagnostic() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshK, setRefreshK] = useState(0);
    const [testEmail, setTestEmail] = useState("");
    const [testSubject, setTestSubject] = useState("Test desde Eclipse Cubiertas");
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        adminApi.get("/diagnostic/env").then((r) => { if (alive) { setData(r.data); setLoading(false); } }).catch((e) => { console.error(e); setLoading(false); });
        return () => { alive = false; };
    }, [refreshK]);

    const sendTest = async () => {
        if (!testEmail) return;
        setTesting(true);
        try {
            const r = await adminApi.api.post("/test-email", { to: testEmail, subject: testSubject, html: "<p>Esto es un email de prueba enviado desde el panel de diagnóstico.</p>" });
            setTestResult(r.data);
            toast.success("Email procesado");
        } catch (e) {
            toast.error("Error al enviar");
        } finally {
            setTesting(false);
        }
    };

    if (loading || !data) return <AdminLayout title="Diagnóstico" testid="admin-diagnostic-page"><div className="text-sm text-neutral-400">Cargando…</div></AdminLayout>;

    const items = [
        { key: "mongo", label: "MongoDB", icon: Database, ok: data.mongodb.connected, info: `BD: ${data.mongodb.db_name}` },
        { key: "jwt", label: "JWT Secret", icon: ShieldCheck, ok: data.jwt_secret.configured, info: data.jwt_secret.configured ? `Configurado (${data.jwt_secret.length} chars)` : "Falta o muy corto" },
        { key: "admin", label: "Admin seed", icon: UserCheck, ok: data.admin_seed.exists, info: data.admin_seed.email || "Sin admin" },
        { key: "resend", label: "Resend", icon: Mail, ok: data.resend.configured, warn: !data.resend.configured, info: data.resend.configured ? `Activa · sender: ${data.resend.sender_email}` : `Sin API key — emails en modo SKIPPED · sender: ${data.resend.sender_email}` },
        { key: "public_url", label: "Public Site URL", icon: Globe, ok: data.public_site_url.configured, info: data.public_site_url.value || "—" },
        { key: "scheduler", label: "Scheduler", icon: Clock, ok: data.scheduler.running, info: data.scheduler.running ? `${data.scheduler.jobs.length} jobs activos` : "Detenido" },
    ];

    return (
        <AdminLayout title="Diagnóstico" subtitle="Estado en tiempo real del sistema y sus integraciones" testid="admin-diagnostic-page" actions={
            <button onClick={() => setRefreshK((x) => x + 1)} className="h-9 px-3 border border-neutral-200 rounded-md text-sm flex items-center gap-2 bg-white hover:bg-neutral-50" data-testid="diag-refresh"><RefreshCw className="w-4 h-4" /> Refrescar</button>
        }>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((it) => {
                    const StatusIcon = it.ok ? CheckCircle2 : it.warn ? AlertCircle : XCircle;
                    const color = it.ok ? "text-green-600" : it.warn ? "text-amber-600" : "text-red-600";
                    const IconBase = it.icon;
                    return (
                        <div key={it.key} className="bg-white border border-neutral-200 rounded-md p-4 flex gap-3" data-testid={`diag-card-${it.key}`}>
                            <div className="shrink-0"><IconBase className="w-5 h-5 text-neutral-500" /></div>
                            <div className="flex-1">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold text-neutral-900">{it.label}</span>
                                    <StatusIcon className={`w-5 h-5 ${color}`} />
                                </div>
                                <p className="text-xs text-neutral-500 mt-1">{it.info}</p>
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="bg-white border border-neutral-200 rounded-md p-5">
                <h3 className="text-sm font-semibold text-neutral-900 mb-3">Scheduler jobs</h3>
                <div className="space-y-1.5 text-xs">
                    {data.scheduler.jobs.length === 0 && <div className="text-neutral-400">Sin jobs registrados</div>}
                    {data.scheduler.jobs.map((j) => (
                        <div key={j.id} className="flex justify-between border-b border-neutral-100 pb-1.5 last:border-0">
                            <span className="font-mono text-neutral-700">{j.id}</span>
                            <span className="text-neutral-500">{j.next_run ? new Date(j.next_run).toLocaleString("es-MX") : "—"}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-md p-5">
                <h3 className="text-sm font-semibold text-neutral-900 mb-3 flex items-center gap-2"><Megaphone className="w-4 h-4 text-eclipse-cta" /> Google Ads por sitio</h3>
                <div className="grid grid-cols-3 gap-3">
                    {Object.entries(data.gads_per_sitio).map(([sitio, enabled]) => (
                        <div key={sitio} className={`border rounded-md p-3 ${enabled ? "border-green-200 bg-green-50" : "border-neutral-200"}`}>
                            <div className="text-xs uppercase tracking-wider text-neutral-500">{sitio}</div>
                            <div className={`text-sm font-semibold mt-1 ${enabled ? "text-green-700" : "text-neutral-500"}`}>{enabled ? "✓ Activo" : "○ Desactivado"}</div>
                        </div>
                    ))}
                </div>
                <div className="mt-3"><Link to="/admin/ads-setup" className="text-xs text-eclipse-cta hover:underline">Configurar →</Link></div>
            </div>

            <div className="bg-white border border-neutral-200 rounded-md p-5">
                <h3 className="text-sm font-semibold text-neutral-900 mb-3 flex items-center gap-2"><Send className="w-4 h-4 text-eclipse-cta" /> Probar envío de email</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Input type="email" placeholder="destinatario@email.com" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} data-testid="diag-test-email-to" />
                    <Input placeholder="Asunto" value={testSubject} onChange={(e) => setTestSubject(e.target.value)} data-testid="diag-test-email-subject" />
                    <button onClick={sendTest} disabled={testing || !testEmail} className="h-9 px-4 bg-eclipse-cta text-white text-sm font-semibold rounded-md flex items-center justify-center gap-2 hover:bg-orange-600 disabled:opacity-50" data-testid="diag-test-email-send">
                        {testing ? "Enviando…" : "Enviar prueba"}
                    </button>
                </div>
                {testResult && (
                    <div className="mt-3 bg-neutral-50 border border-neutral-200 rounded p-3 text-xs flex flex-wrap items-center gap-3" data-testid="diag-test-email-result">
                        <span>Estado:</span>
                        <StatusBadge label={testResult.sent ? "Enviado" : testResult.skipped ? "Skipped (sin API key)" : "Fallido"} color={testResult.sent ? "bg-green-100 text-green-700" : testResult.skipped ? "bg-neutral-100 text-neutral-600" : "bg-red-100 text-red-700"} />
                        {testResult.log_id && <span className="font-mono text-neutral-500">log: {testResult.log_id.slice(0, 8)}</span>}
                        {testResult.error && <span className="text-red-600">{testResult.error}</span>}
                        <Link to="/admin/emails" className="text-eclipse-cta hover:underline ml-auto">Ver log completo →</Link>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}
